import { useMutation } from "@apollo/client";
import {
  Button,
  CurrencyAmountInput,
  PrimaryFullScreenSpinner,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import { dateSchema } from "@repo/utils";
import { useStore } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { Suspense } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { currencyValues } from "~/gql/client/enumValues";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/ledger/transactions_/new")({
  head: () => opsHead("New Ledger Transaction"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const formSchema = z
  .object({
    fromLedgerAccountId: z.string().nonempty("Required"),
    toLedgerAccountId: z.string().nonempty("Required"),
    amount: z
      .custom<Decimal>(Decimal.isDecimal, {
        message: "Please enter an amount",
      })
      .refine((value) => value.gt(0), "Amount must be greater than 0"),
    unit: z.enum(currencyValues),
    effectiveAt: dateSchema,
    reference: z.string().optional(),
    description: z.string().optional(),
  })
  .refine((values) => values.fromLedgerAccountId !== values.toLedgerAccountId, {
    message: "Accounts must be different",
    path: ["toLedgerAccountId"],
  });

type FormData = z.infer<typeof formSchema>;

function View() {
  const navigate = useNavigate();

  const [createLedgerEntryPairMutation, { loading }] = useMutation(
    graphql(`
      mutation AdminNewLedgerTransactionForm_Create(
        $input: CreateNewLedgerEntryPairInput!
      ) {
        createNewLedgerEntryPair(input: $input) {
          ledgerTransaction {
            id
            shortRef
          }
        }
      }
    `),
  );

  const form = useAppForm({
    defaultValues: {
      fromLedgerAccountId: "",
      toLedgerAccountId: "",
      unit: "AUD",
      reference: "",
      description: "",
    } as FormData,
    onSubmit: async ({ value }) => {
      const parsedData = formSchema.parse(value);
      const defaultErrorMessage =
        "There was an error submitting your form. Please try again later.";

      await createLedgerEntryPairMutation({
        variables: {
          input: {
            amount: parsedData.amount,
            unit: parsedData.unit,
            fromLedgerAccountId: parsedData.fromLedgerAccountId,
            toLedgerAccountId: parsedData.toLedgerAccountId,
            effectiveAt: parsedData.effectiveAt,
            reference: parsedData.reference,
            description: parsedData.description,
          },
        },
        onCompleted: (result) => {
          const ledgerTransaction =
            result.createNewLedgerEntryPair?.ledgerTransaction;
          if (!ledgerTransaction?.id) {
            toast({
              kind: "error",
              title: "Failed to create ledger transaction",
              description: defaultErrorMessage,
            });
            throw new Error("failed to create ledger transaction");
          }
          toast({
            kind: "success",
            title: "Created ledger transaction",
            description: `Created ${ledgerTransaction.shortRef ?? ledgerTransaction.id}`,
          });
          navigate({ to: `/ledger/transactions/${ledgerTransaction.id}` });
        },
        onError: (error) => {
          const errorMessage =
            error.graphQLErrors?.[0]?.message || defaultErrorMessage;
          toast({
            kind: "error",
            title: "Failed to create ledger transaction",
            description: errorMessage,
          });
          throw error;
        },
      });
    },
  });

  const selectedUnit = useStore(form.store, (state) => state.values.unit);

  return (
    <AdminPageContainer testId="admin-ledger-transactions-new">
      <AdminPageHeader
        title="New Ledger Transaction"
        subtitle="Create a new ledger transaction"
      />
      <form.AppForm>
        <form
          className="flex max-w-2xl flex-col gap-6"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.AppField name="fromLedgerAccountId">
            {(field) => (
              <field.InputField
                label={
                  <Txt size="sm" weight="semibold">
                    From ledger account ID
                  </Txt>
                }
                placeholder="ledger_account_..."
              />
            )}
          </form.AppField>

          <form.AppField name="toLedgerAccountId">
            {(field) => (
              <field.InputField
                label={
                  <Txt size="sm" weight="semibold">
                    To ledger account ID
                  </Txt>
                }
                placeholder="ledger_account_..."
              />
            )}
          </form.AppField>

          <form.AppField name="amount">
            {(field) => (
              <div className="flex flex-col gap-2">
                <Txt size="sm" weight="semibold">
                  Amount
                </Txt>
                <CurrencyAmountInput
                  value={field.state.value ?? null}
                  onValueChange={(newValue) =>
                    field.handleChange(newValue ?? new Decimal(0))
                  }
                  currency={selectedUnit ?? "AUD"}
                  selectableCurrency
                  onCurrencyChange={(currency) =>
                    form.setFieldValue("unit", currency)
                  }
                  placeholder="0.00"
                />
              </div>
            )}
          </form.AppField>

          <form.AppField name="effectiveAt">
            {(field) => (
              <field.InputField
                type="datetime-local"
                label={
                  <Txt size="sm" weight="semibold">
                    Effective at
                  </Txt>
                }
              />
            )}
          </form.AppField>

          <form.AppField name="reference">
            {(field) => (
              <field.InputField
                label={
                  <Txt size="sm" weight="semibold">
                    Reference
                  </Txt>
                }
                placeholder="Optional"
              />
            )}
          </form.AppField>

          <form.AppField name="description">
            {(field) => (
              <field.TextareaField
                label={
                  <Txt size="sm" weight="semibold">
                    Description
                  </Txt>
                }
                placeholder="Optional"
              />
            )}
          </form.AppField>

          <div className="flex justify-end">
            <Button type="submit" disabled={loading}>
              Create ledger transaction
            </Button>
          </div>
        </form>
      </form.AppForm>
    </AdminPageContainer>
  );
}
